import { Component, computed, inject } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { IconComponent } from '@kin/spark-ui';
import { SignupAddressStore } from './signup-address.store';

@Component({
  selector: 'app-savings-banner',
  standalone: true,
  imports: [DecimalPipe, IconComponent],
  template: `
    <section class="banner">
      <div class="hood-pill">
        <ngk-icon icon="map-pin" size="12px" />{{ neighborhood() }}
      </div>
      <div class="overline">Average savings for homes near you</div>
      <div class="amount">\${{ store.lookup()?.avgSavings | number:'1.0-0' }}<span class="per-year">/yr</span></div>
      <div class="note">
        Based on data for {{ neighborhood() }}.
        <strong>This is just an estimate. Your actual savings depend on your assessment.</strong>
      </div>
      <div class="address">
        <ngk-icon icon="house" size="16px" /><span>{{ store.singleLineAddress() }}</span>
      </div>
    </section>
  `,
  styles: `
    @keyframes savingsRise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    /* Approved pairing 1: Yellow-600 background, Neutral-1000 text (12.20:1) */
    .banner {
      background: var(--kin-yellow-600); color: var(--kin-neutral-1000);
      border-radius: var(--kin-radius-card); padding: 40px 32px; margin-bottom: 24px;
      text-align: center; animation: savingsRise 400ms var(--kin-ease-out); box-shadow: var(--kin-shadow-md);
    }
    /* Approved pairing 3 inverted: Neutral-1000 pill with white text (18.76:1) */
    .hood-pill {
      display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: var(--kin-radius-pill);
      background: var(--kin-neutral-1000); color: var(--kin-white);
      font-size: 12px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 16px;
    }
    .overline { font-size: 14px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 12px; }
    .amount {
      font-size: 56px; font-weight: 700; line-height: 1.05; letter-spacing: -2px;
      font-variant-numeric: tabular-nums; margin-bottom: 12px;
    }
    .per-year { font-size: 22px; font-weight: 600; letter-spacing: 0; }
    .note { font-size: 14px; line-height: 1.5; max-width: 460px; margin: 0 auto; }
    .note strong { font-weight: 700; }
    .address {
      margin-top: 18px; padding-top: 16px; border-top: 1px solid rgba(18, 18, 15, 0.15);
      font-size: 14px; font-weight: 600;
      display: flex; align-items: center; justify-content: center; gap: 8px;
      text-transform: capitalize;
    }
    @media (max-width: 600px) {
      .banner { padding: 32px 20px; }
      .amount { font-size: 40px; letter-spacing: -1.5px; }
    }
  `,
})
export class SavingsBannerComponent {
  // Address + lookup (avgSavings) consumed from the query string (see SignupAddressStore)
  protected store = inject(SignupAddressStore);

  // Note: the /lookup doesn't name the neighborhood yet, so fall back to the city
  protected readonly neighborhood = computed(() => this.store.address()?.city ?? '');
}
